import React, { useState, useEffect, useRef } from "react";
import { injectIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import { Box, Typography, Stack, Tabs, Tab } from "@mui/material";
import { Icon } from "@iconify/react";
import { inject, observer } from "mobx-react";
import { COMMON_UTIL } from "../../../common/util/common.util";
import { DATE_UTIL } from "../../../common/util/date.util";
import { 
  TodaySituation, 
  HomeContBox,
  BoxWrap, 
  StatusBox, 
  MoreButton,
  UsageCard,
  UsageCardHeader,
  UsageProgressBar
} from "../Styled/HomeStyled";

import BarChartComponent from "../../CommonComponent/Chart/BarChart";
import WordCloudComponent from "../../CommonComponent/Chart/WordCloud";
import StatsIcon01 from "@/common/Images/StatsIcon01.svg";
import StatsIcon02 from "@/common/Images/StatsIcon02.svg";
import StatsIcon03 from "@/common/Images/StatsIcon03.svg";
import { c } from "../../../theme/colorUtils";

// TabPanel 컴포넌트
function TabPanel({ children, value, index, ...other }) {
  const isActive = value === index;

  return (
    <div
      role="tabpanel"
      id={`home-stats-tabpanel-${index}`}
      aria-labelledby={`home-stats-tab-${index}`}
      hidden={!isActive}
      style={{
        maxHeight: '250px',
        visibility: isActive ? 'visible' : 'hidden',
        position: isActive ? 'relative' : 'absolute',
        opacity: isActive ? 1 : 0,
        transition: 'opacity 0.3s ease-in-out',
        pointerEvents: isActive ? 'auto' : 'none',
      }}
      {...other}
    >
      {isActive && children}
    </div>
  );
}




// UsageStatusCard 컴포넌트
function UsageStatusCard({ icon, label, value, unit, maxValue = 100, progressValue, delay = 0, isVisible = false, barColor }) {
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const progress = progressValue !== undefined ? progressValue : (typeof value === 'number' ? value : 0);

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setAnimatedProgress(progress);
      }, delay);

      return () => clearTimeout(timer);
    } else {
      setAnimatedProgress(0);
    }
  }, [progress, delay, isVisible]);

  return (
    <UsageCard>
      <UsageCardHeader>
        <Stack direction="row" alignItems="center" gap={1}>
          <img src={icon} alt={label}/>
          <Typography variant="body1" color={c.text01}>{label}</Typography>
        </Stack>
        <Typography variant="body2" color={c.text02}>
          {value}{unit && `\u00A0${unit}`}
        </Typography>
      </UsageCardHeader>
      <UsageProgressBar
        progress={animatedProgress}
        maxProgress={maxValue}
        barColor={barColor}
      />
    </UsageCard>
  );
}

function a11yProps(index) {
  return {
    id: `home-stats-tab-${index}`,
    'aria-controls': `home-stats-tabpanel-${index}`,
  };
}

const HomeStats = ({
  intl,
  homeStore,
  pronunciationStore,
  reportStore,
  languageStore
}) => {
  const history = useHistory();
  const view = "daily";
  const [tabValue, setTabValue] = useState(0);
  const [isUsageVisible, setIsUsageVisible] = useState(false);
  const [showWordCloud, setShowWordCloud] = useState(false);
  const usageStatsRef = useRef(null);

  const { usageStatus, pronunciationGraphList, topicWordList } = homeStore.homeData;



  // Intersection Observer 설정 - tabValue가 0일 때만 실행
  useEffect(() => {
    if (tabValue !== 0) {
      setIsUsageVisible(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsUsageVisible(true);
        }
      },
      {
        threshold: 0.3,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    if (usageStatsRef.current) {
      observer.observe(usageStatsRef.current);
      // 탭 전환 시 즉시 체크
      const rect = usageStatsRef.current.getBoundingClientRect();
      const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
      if (isInViewport) {
        setIsUsageVisible(true);
      }
    }

    return () => {
      if (usageStatsRef.current) {
        observer.unobserve(usageStatsRef.current);
      }
    };
  }, [tabValue]);

  useEffect(() => {
    if (tabValue === 2) {
      const timer = setTimeout(() => setShowWordCloud(true), 200);
      return () => clearTimeout(timer);
    } else {
      setShowWordCloud(false);
    }
  }, [tabValue]);


  const handleClickPronunciation = () => {
    pronunciationStore.setSelectedTab("daily");
    history.push("/pronunciation");
  };

  const handleClickMoveReport = () => {
    reportStore.setSelectedTab("daily");
    history.push("/report");
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <TodaySituation>
      <HomeContBox width='100%' p={3}>
        <Typography variant="subtitle1" fontWeight={500} mb={1}>
          {DATE_UTIL.getTimeZoneDate(
            homeStore.selectedCalendarDate,
            DATE_UTIL.FORMAT.DAY_FORMAT
          )}
        </Typography>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="홈 통계 탭"
            variant="fullWidth"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: c.primary,
              },
              '& .MuiTab-root': {
                color: '#BFBFBF!important',
                fontSize: '1rem',
                padding: '8px',
              },
              '& .Mui-selected': {
                color: `${c.text01} !important`,
              },
            }}
          >
            <Tab
              label={intl.formatMessage({ id: "usage_status" })}
              {...a11yProps(0)}
            />
            <Tab
              label={intl.formatMessage({ id: "pronunciation_evaluation" })}
              {...a11yProps(1)}
            />
            <Tab
              label={intl.formatMessage({ id: "interest_keywords" })}
              {...a11yProps(2)}
            />
          </Tabs>
        </Box>

        {/* 사용 현황 탭 */}
        <TabPanel value={tabValue} index={0}>
          <BoxWrap aria-label="홈-오늘 사용현황" ref={usageStatsRef}>
            <StatusBox>
              <UsageStatusCard
                icon={StatsIcon01}
                label={intl.formatMessage({ id: "dialog_count" })}
                value={usageStatus?.dialogCount || 0}
                unit={intl.formatMessage({ id: "home_count_unit" })}
                maxValue={100}
                progressValue={usageStatus?.dialogCount || 0}
                delay={200}
                isVisible={isUsageVisible}
                barColor="#C3D8FF"
              />
              <UsageStatusCard
                icon={StatsIcon02}
                label={intl.formatMessage({ id: "content_usage" })}
                value={usageStatus?.bookCount || 0}
                unit={intl.formatMessage({ id: "book_unit" })}
                maxValue={20}
                progressValue={usageStatus?.bookCount || 0}
                delay={400}
                isVisible={isUsageVisible}
                barColor="#E4CFFF"
              />
              <UsageStatusCard
                icon={StatsIcon03}
                label={intl.formatMessage({ id: "total_usage_time" })}
                value={COMMON_UTIL.convertSecondsToTime(
                  intl,
                  usageStatus?.totalUsageTime || 0
                )}
                unit=""
                progressValue={usageStatus?.totalUsageTime || 0}
                maxValue={18000}
                delay={600}
                isVisible={isUsageVisible}
                barColor="#FFD0CC"
              />
            </StatusBox>
            <MoreButton
                onClick={handleClickMoveReport}
                style={{
                  visibility:
                      usageStatus?.dialogCount > 0 ||
                      usageStatus?.bookCount > 0 ||
                      usageStatus?.totalUsageTime > 0
                          ? "visible"
                          : "hidden",
                }}
            >
              {intl.formatMessage({ id: "see_more" })}
              <Icon icon="iconamoon:arrow-right-2-thin" fontSize={16} />
            </MoreButton>
          </BoxWrap>
        </TabPanel>

        {/* 발음 평가 탭 */}
        <TabPanel value={tabValue} index={1}>
          <BoxWrap aria-label="홈-발음평가">
            {tabValue === 1 && (
              <>
                <BarChartComponent
                  languageStore={languageStore}
                  view={view}
                  pronunciationData={
                    homeStore.homeData ? pronunciationGraphList : []
                  }
                />
                <MoreButton
                    onClick={handleClickPronunciation}
                    style={{
                      visibility:
                          homeStore.homeData && !(pronunciationGraphList.length === 0 || (pronunciationGraphList.every(data => data.score === 0)))
                              ? "visible"
                              : "hidden",
                    }}
                >
                  {intl.formatMessage({ id: "see_more" })} {/* 자세히 보기 */}
                  <Icon icon="iconamoon:arrow-right-2-thin" fontSize={16}/>
                </MoreButton>
              </>
            )}
          </BoxWrap>
        </TabPanel>

        {/* 관심 키워드 탭 */}
        <TabPanel value={tabValue} index={2}>
          <BoxWrap aria-label="홈-관심키워드">
            <Box className="wordcloud" sx={{ minHeight: 250 }}>
              {showWordCloud && (
                <WordCloudComponent showWordCloud={showWordCloud} wordList={topicWordList} />
              )}
            </Box>
          </BoxWrap>
        </TabPanel>
      </HomeContBox>
    </TodaySituation>
  );
};

export default injectIntl(
  inject("homeStore", "pronunciationStore", "reportStore", "languageStore")(observer(HomeStats))
);
